import KYCFlowMaxErrors from '@/src/common/components/elements/Alerts/KYCFlowMaxErrors'
import { TextInputWithLabel } from '@/src/common/components/elements/Forms/TextInputWithLabel'
import Button from '@/src/common/components/marketing/buttons/Button'
import { useCartContext } from '@/src/common/contexts/CartContext'
import { useGiftQuantityContext } from '@/src/common/contexts/GiftQuantityContext'
import { FormErrorDisplay } from '@/src/modules/build-a-card/FormErrorDisplay'
import {
    DEFAULT_IMAGE_TYPE,
    nameMax,
} from '@/src/modules/build-a-card/constants'
import DefaultDenominationOptions from '@/src/modules/build-a-card/inputs/DefaultDenominationOptions'
import { TMinMaxFromGYPMerchant } from '@/src/modules/build-a-card/types'
import { PGPlusStep1SchemaWithFixedDenoms } from '@/src/modules/build-a-card/validations/schemas'
import { StyledTextArea } from '@/src/modules/pg-plus/PGPlusStyledComponents'
import {
    MAX_PG_PLUS_SINGLE_CARD_LOAD,
    MIN_PG_PLUS_CARD_LOAD,
} from '@/src/modules/pg-plus/constants'
import trimStart from 'lodash/trimStart'
import { useRouter } from 'next/router'
import { BaseSyntheticEvent, useEffect, useState } from 'react'
import { ValidationError } from 'yup'
import { useBasicGiftCardContext } from '@/src/common/contexts/BasicGiftCardContext'
import PurchaseFlowMaxAlert from '@/src/common/components/elements/Alerts/PurchaseFlowMaxAlert'
import ImageSelectorButtons from '@/src/common/components/elements/Buttons/ImageSelectorButtons'
import { validateDenomOnChange } from '@/src/modules/build-a-card/validations/validateDenom'
import { Alert } from '@/src/common/components/elements/Forms/Alert'
import { determinePartnerIncentiveValues } from '@/src/modules/partners/utilities/determinePartnerIncentiveValues'
import { DenomInputWithLabel } from '@/src/common/components/elements/Forms/DenomInputWithLabel'
import { HeaderParagraphText } from '@/src/modules/build-a-card/StyledComponents'
import {
    FieldWrapper,
    FieldWrapperWithGap,
} from '@/src/common/components/elements/Forms/StyledComponents'
import KYCMaxPurchaseButtons from '@/src/modules/kyc/buttons/KYCMaxPurchaseButtons'
import { SimpleFieldWrapper } from '@/src/modules/build-a-card/StepElement'
import isEmpty from 'lodash/isEmpty'

interface IPGPlusFormProps {
    setCurrentStep: Function
    minMaxAndFixed: TMinMaxFromGYPMerchant
}

const PGPlusFormStep1 = ({
    setCurrentStep,
    minMaxAndFixed,
}: IPGPlusFormProps) => {
    const {
        senderName,
        setSenderName,
        denomination,
        setDenomination,
        recipientMessage,
        setRecipientMessage: setMessage,
        designImage,
        selectedImageType,
        setSelectedImageType,
        partnerData,
    } = useBasicGiftCardContext()
    const { giftState, KYCMinMaxLimits } = useGiftQuantityContext()
    const {
        loadAmountExceedsKYCMax,
        numberOfCardsExceedsKYCMax,
        loadAmountMeetsKYCMin,
        numberOfCardsMeetsKYCMin,
    } = KYCMinMaxLimits
    const { cardQuantity } = giftState
    const { cartTotalCards, setCartFlyoutOpen } = useCartContext()

    const router = useRouter()
    const [controlsInteractedWith, setControlsInteractedWith] =
        useState<boolean>(false)

    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [displayLiveErrorsInStep1, setDisplayLiveErrorsInStep1] =
        useState<boolean>(false)
    const [formErrors, setFormErrors] = useState<ValidationError[]>([])

    const showKYCMaxError: boolean =
        loadAmountExceedsKYCMax || numberOfCardsExceedsKYCMax
    const showKycLimitAlert =
        (loadAmountMeetsKYCMin || numberOfCardsMeetsKYCMin) && !showKYCMaxError

    const { brand_min, brand_max, egift } = minMaxAndFixed
    const minValue = brand_min ? brand_min : MIN_PG_PLUS_CARD_LOAD
    const maxValue = brand_max ? brand_max : MAX_PG_PLUS_SINGLE_CARD_LOAD

    // if in partner flow and brand only allows fixed egift denoms, hide custom input
    const fixedValues = partnerData && egift?.fixed?.length ? egift?.fixed : undefined
    const showDiscountText =
        !isEmpty(partnerData) &&
        (partnerData?.programConfig?.show_discount_text ?? true)

    useEffect(() => {
        if (!controlsInteractedWith && (cardQuantity > 1 || !!denomination)) {
            setControlsInteractedWith(true)
        }
    }, [cardQuantity, controlsInteractedWith, denomination])

    useEffect(() => {
        if (!displayLiveErrorsInStep1) {
            return
        }

        PGPlusStep1SchemaWithFixedDenoms(minValue, maxValue, fixedValues)
            .validate(
                {
                    denomination,
                    designImage,
                    senderName,
                    recipientMessage,
                },
                { abortEarly: false }
            )
            .then(() => {
                setFormErrors([])
            })
            .catch((err) => {
                setFormErrors(err.inner)
            })
    }, [
        denomination,
        designImage,
        displayLiveErrorsInStep1,
        recipientMessage,
        senderName,
        minValue,
        maxValue,
        fixedValues,
    ])

    const continueToStep2 = async () => {
        await PGPlusStep1SchemaWithFixedDenoms(minValue, maxValue, fixedValues)
            .validate(
                {
                    denomination,
                    designImage,
                    senderName,
                    recipientMessage,
                },
                {
                    abortEarly: false,
                    context: { checkLanguage: true },
                }
            )
            .then(() => {
                setFormErrors([])
                setCurrentStep(2)

                // if using default image, set selected image type to default
                // this ensures the wrapper shows when moving back to step 1 + resolves
                // an issue with the loader if unwrapped and navigating between 1 and 2
                !selectedImageType && setSelectedImageType(DEFAULT_IMAGE_TYPE)
            })
            .catch((err) => {
                setFormErrors(err.inner)
                setDisplayLiveErrorsInStep1(true)
            })
            .finally(() => setIsLoading(false))
    }

    return (
        <FieldWrapperWithGap>
            <FieldWrapper>
                <DefaultDenominationOptions
                    denomination={denomination}
                    onDenominationChange={(denom) =>
                        validateDenomOnChange({
                            denom,
                            setDenomination,
                            formErrors,
                            setFormErrors,
                            minDenom: brand_min,
                            maxDenom: brand_max,
                            fixedDenoms: fixedValues,
                        })
                    }
                    minMaxAndFixed={minMaxAndFixed}
                    label={'1. Choose amount:'}
                    partner={partnerData?.partnerName}
                />
                {!fixedValues && (
                    <DenomInputWithLabel
                        name={'denomination'}
                        label={'Custom Amount'}
                        data-testid={'denomination'}
                        placeholder={`Enter an amount $${minValue} - $${maxValue}`}
                        value={denomination}
                        onChange={(e: BaseSyntheticEvent) => {
                            const denom = e.target.value
                            validateDenomOnChange({
                                denom,
                                setDenomination,
                                formErrors,
                                setFormErrors,
                                minDenom: brand_min,
                                maxDenom: brand_max,
                                fixedDenoms: fixedValues,
                            })
                        }}
                        onBlur={() => {
                            // if denom set, adjust format to ###.##
                            if (denomination) {
                                setDenomination(
                                    parseFloat(denomination).toFixed(2)
                                )
                            }
                        }}
                        type={'number'}
                        aria-placeholder={'Enter a custom amount'}
                        min={brand_min}
                        max={brand_max}
                    />
                )}
                <FormErrorDisplay
                    formErrors={formErrors}
                    path={'denomination'}
                />
                {showDiscountText && (
                    <Alert
                        variant={'discount'}
                        label={determinePartnerIncentiveValues(
                            partnerData,
                            denomination
                        )}
                    />
                )}
            </FieldWrapper>
            <FieldWrapper>
                <HeaderParagraphText>
                    2. Choose wrapper design:
                </HeaderParagraphText>
                <ImageSelectorButtons formErrors={formErrors} />
            </FieldWrapper>
            <FieldWrapperWithGap>
                <FieldWrapper>
                    <HeaderParagraphText>
                        3. Write a gift message:
                    </HeaderParagraphText>
                    <StyledTextArea
                        isRequired={true}
                        label={'Message'}
                        data-testid={'message'}
                        value={recipientMessage}
                        onChange={(e: BaseSyntheticEvent) => {
                            const trimmedValue = trimStart(
                                e.currentTarget.value
                            )
                            setMessage(trimmedValue)
                        }}
                        maxLength={150}
                        placeholder={'Add a message...'}
                        charCounter={true}
                        charMax={150}
                    />
                    <FormErrorDisplay
                        formErrors={formErrors}
                        path={'recipientMessage'}
                    />
                </FieldWrapper>
                <FieldWrapper>
                    <TextInputWithLabel
                        isRequired={true}
                        label={'From'}
                        autoComplete={'off'}
                        data-testid={'sender-name'}
                        value={senderName}
                        maxLength={nameMax}
                        placeholder={'Add a sender name...'}
                        onChange={(e: BaseSyntheticEvent) => {
                            const trimmedValue = trimStart(
                                e.currentTarget.value
                            )
                            setSenderName(trimmedValue)
                        }}
                    />
                    <FormErrorDisplay
                        formErrors={formErrors}
                        path={'senderName'}
                    />
                    {showKYCMaxError && <KYCFlowMaxErrors />}
                    {showKycLimitAlert && <PurchaseFlowMaxAlert />}
                </FieldWrapper>
                <SimpleFieldWrapper>
                    {showKYCMaxError ? (
                        <KYCMaxPurchaseButtons
                            isLoading={isLoading}
                            setIsLoading={setIsLoading}
                            cartTotalCards={cartTotalCards}
                            setCartFlyoutOpen={setCartFlyoutOpen}
                            router={router}
                        />
                    ) : (
                        <Button
                            id={'next-button'}
                            label={'Next'}
                            type={'button'}
                            variant={'primary'}
                            size={'md'}
                            width={'standard'}
                            textTransform={'uppercase'}
                            showArrow
                            disabled={isLoading}
                            isLoading={isLoading}
                            onClick={() => {
                                setIsLoading(true)
                                continueToStep2().then()
                            }}
                        />
                    )}
                </SimpleFieldWrapper>
            </FieldWrapperWithGap>
        </FieldWrapperWithGap>
    )
}

export default PGPlusFormStep1
